import { CarePhase } from '../../../appModel';
import { appliedHealthCareInflation } from '../../../appliedHealthCareInflation';
import { calcMonthlyCareInflatedValue } from './phaseCarePlanCalculations';
import {
  DAYS_IN_MONTH,
  ONE_ADL_DURATION_MULTIPLIER,
  POLICY_ACTIVATES_AT_ADL_COUNT,
} from './sharedConstants';

// Per-row count of "non-2-ADL" days — days where the client has < 2 ADLs
// and so isn't eligible for benefit payment. The ineligible bucket sits
// at the start of the phase. Strategies whose products don't use the
// 2-ADL gate can skip this helper.
export function computeNon2AdlDaysThisRow(
  client: Client,
  phaseCarePlan: PhaseCarePlan,
  monthsIntoPhase: number,
): number {
  const phaseCareDays = Math.round(phaseCarePlan.phaseCareMonthsNeeded) * DAYS_IN_MONTH;
  const clientAdlCount = client.intakeSurvey?.clientAdlReceivedAssistanceCount ?? 0;
  const has2AdlGate =
    phaseCarePlan.carePhase === CarePhase.earlyCare &&
    clientAdlCount < POLICY_ACTIVATES_AT_ADL_COUNT;
  if (!has2AdlGate) return 0;
  const non2AdlBucketEnd = phaseCareDays * ONE_ADL_DURATION_MULTIPLIER;
  const dayStart = monthsIntoPhase * DAYS_IN_MONTH;
  const dayEnd = dayStart + DAYS_IN_MONTH;
  return Math.max(0, Math.min(dayEnd, non2AdlBucketEnd) - Math.max(dayStart, 0));
}

export interface MonthlyCostMetrics {
  nominalCareCost: number;
  effectiveCareCost: number;
  dailyCareCost: number;
}

// Per-month effective care cost. Inflates the phase's nominal
// professional-share cost from *today* (client added date) to the row's
// calendar position. Anchored at the phase's predicted start year +
// monthsIntoPhase, so the cost shown in row 0 of a phase already
// reflects all the inflation accrued between today and that phase
// starting. Strategies that need a different cost model can reimplement.
export function computeMonthlyCostMetrics(
  client: Client,
  phaseCarePlan: PhaseCarePlan,
  monthsIntoPhase: number,
): MonthlyCostMetrics {
  const phaseCosts = client.phaseCosts?.find(p => p.carePhase === phaseCarePlan.carePhase);
  const nominalPhaseCost = phaseCosts?.phaseProfessionalShareCost ?? 0;
  const phaseMonths = Math.max(Math.round(phaseCarePlan.phaseCareMonthsNeeded), 1);
  const nominalCareCost = nominalPhaseCost / phaseMonths;
  const inflationRate = appliedHealthCareInflation(client);

  // Years from today to this row, anchored at the phase's predicted
  // start year. Annual-stepped: a row's cost holds flat across the 12
  // months following each integer-year anniversary. Matches carrier
  // illustrations (annual tables).
  const currentYear = client.clientAddedDateTime
    ? client.clientAddedDateTime.getFullYear()
    : new Date().getFullYear();
  const phaseStartYear =
    client.clientPhasePredictedStartYears?.[phaseCarePlan.carePhase] ?? currentYear;
  const yearsFromTodayContinuous =
    phaseStartYear - currentYear + monthsIntoPhase / 12;
  const yearsFromToday = Math.max(Math.floor(yearsFromTodayContinuous), 0);

  const effectiveCareCost = calcMonthlyCareInflatedValue(
    nominalCareCost,
    inflationRate,
    yearsFromToday * 12,
  );
  return {
    nominalCareCost,
    effectiveCareCost,
    dailyCareCost: effectiveCareCost / DAYS_IN_MONTH,
  };
}
